import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { setTimeout$ } from '@app/helpers/timeout';
import {
  AddressSuggestion,
  LocationDisplay,
  StreetDisplay,
} from '@app/models/address.model';
import { GaService } from '@app/services/ga.service';
import { FormAutofillService } from '@app/services/form-autofill.service';
import { SwissPostAddressService } from '@app/services/swisspost-address.service';
import { WebformService } from '@app/services/webform.service';
import { MobiUiTypeaheadComponent } from '@mobi/rwc-ui-components-ng-jslib';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
@Component({
  standalone: false,
  selector: 'mobi-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressComponent),
      multi: true,
    },
  ],
})
export class AddressComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
  @Output() readonly valueChange = new EventEmitter<Record<string, string>>();

  @Input() field: Record<string, any> = {};
  @Input() parentForm!: FormGroup;

  @ViewChild('postalCityTypeahead') postalCityTypeahead!: MobiUiTypeaheadComponent;
  @ViewChild('addressTypeahead') addressTypeahead!: MobiUiTypeaheadComponent;

  elements!: Record<string, any>;
  values: Record<string, string> = {};
  loading: Record<string, boolean> = {};
  popover = false;
  alternativeAddresses: AddressSuggestion[] = [];
  postalCity$?: Observable<string[]>;
  address$?: Observable<string[]>;
  hidden = false;

  /** Selected ONRP for Swiss Post API town filtering */
  #selectedOnrp = '';
  /** Selected STRID for Swiss Post API street filtering */
  #selectedStrid = '';

  /** Lookup map for location typeahead selections */
  readonly #locationLookup = new Map<string, LocationDisplay>();
  /** Lookup map for street typeahead selections */
  readonly #streetLookup = new Map<string, StreetDisplay>();

  /** Tracks if the last location search returned no results (invalid combination) */
  #lastLocationSearchEmpty = false;

  #debounceChangeSub?: Subscription;
  #formDataSub?: Subscription;

  /**
   * Custom validator to ensure both postal_code and city are set
   */
  #postalCityValidator(): ValidatorFn {
    return (): ValidationErrors | null => {
      const hasPostalCode = Boolean(this.values?.postal_code?.trim());
      const hasCity = Boolean(this.values?.city?.trim());
      const hasTypedValue = Boolean(this.values?.postal_city?.trim());

      // If user typed something but API returned no results, it's an invalid combination
      if (hasTypedValue && this.#lastLocationSearchEmpty && !hasPostalCode && !hasCity) {
        return { invalidPostalCity: true };
      }

      // If user typed something but didn't select from dropdown
      if (hasTypedValue && (!hasPostalCode || !hasCity)) {
        return { incompletePostalCity: true };
      }

      return null;
    };
  }

  readonly #cdr = inject(ChangeDetectorRef);
  readonly #swissPostService = inject(SwissPostAddressService);
  readonly #ga = inject(GaService);
  readonly #autofill = inject(FormAutofillService);
  readonly #webform = inject(WebformService);

  async ngOnInit(): Promise<void> {
    this.hidden = this.#webform.hiddenFields.includes(this.field['#webform_key']);
    this.elements = this.field['#webform_composite_elements'] || {};
    Object.keys(this.elements).forEach((key: string) => {
      this.values[key] = '';
      this.loading[key] = false;
    });

    // Add combined postal_city control
    this.parentForm.addControl(
      this.postalCityId,
      new FormControl(null, {
        validators: [
          !this.hidden && this.field['#required']
            ? Validators.required
            : Validators.nullValidator,
          this.#postalCityValidator(),
        ],
      })
    );
    this.parentForm.addControl(
      this.addressId,
      new FormControl(null, [
        !this.hidden && this.field['#required']
          ? Validators.required
          : Validators.nullValidator,
      ])
    );
    this.parentForm.addControl(
      this.address2Id,
      new FormControl(null, [Validators.nullValidator])
    );

    this.#formDataSub = this.#webform.formData
      .pipe(distinctUntilChanged(), debounceTime(500))
      .subscribe(() => {
        setTimeout$(() => this.#updateValidators(), 20);
      });

    const data: any = localStorage.getItem(`ng-persist-formdata-${this.#webform.formId}`);
    if (!this.#webform.shouldPersist || !data) return;
    const parsed: any = JSON.parse(data);
    if (parsed?.formData?.[this.field['#webform_id']]) {
      const persisted = parsed?.formData?.[this.field['#webform_id']];
      this.writeValue(persisted);
    }
  }

  #updateValidators(): void {
    this.hidden = this.#webform.hiddenFields.includes(this.field['#webform_id']);
    const requiredValidator =
      this.hidden || !this.field['#required']
        ? Validators.nullValidator
        : Validators.required;
    this.parentForm
      .get(this.postalCityId)
      ?.setValidators([requiredValidator, this.#postalCityValidator()]);
    this.parentForm.get(this.postalCityId)?.updateValueAndValidity();
    this.parentForm.get(this.addressId)?.setValidators(requiredValidator);
    this.parentForm.get(this.addressId)?.updateValueAndValidity();
    this.#cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.#debounceChangeSub?.unsubscribe();
    this.#formDataSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    // Load data for combined postal code + city typeahead using Swiss Post API
    this.postalCity$ = outputToObservable(
      this.postalCityTypeahead?.searchTermChange
    ).pipe(
      debounceTime(150),
      filter((searchTerm: string) => searchTerm?.trim().length >= 2),
      distinctUntilChanged(),
      tap(() => (this.loading.postal_code = true)),
      switchMap((searchTerm: string) => this.#searchLocations(searchTerm)),
      tap(() => (this.loading.postal_code = false))
    );

    // Load data for address/street typeahead using Swiss Post API
    this.address$ = outputToObservable(this.addressTypeahead?.searchTermChange).pipe(
      debounceTime(150),
      filter(
        (searchTerm: string) =>
          searchTerm?.trim().length >= 2 &&
          Boolean(this.values.city || this.values.postal_code)
      ),
      distinctUntilChanged(),
      tap(() => (this.loading.address = true)),
      switchMap((searchTerm: string) => this.#searchStreets(searchTerm)),
      tap(() => (this.loading.address = false))
    );
  }

  /**
   * Search for locations (PLZ + town) using Swiss Post API service
   */
  #searchLocations(searchTerm: string): Observable<string[]> {
    return this.#swissPostService.searchLocations(searchTerm).pipe(
      tap(({ lookupMap, displayStrings }) => {
        this.#locationLookup.clear();
        lookupMap.forEach((value, key) => this.#locationLookup.set(key, value));
        // Track if API returned no results
        this.#lastLocationSearchEmpty = displayStrings.length === 0;
      }),
      map(({ displayStrings }) => displayStrings)
    );
  }

  /**
   * Search for streets using Swiss Post API service
   */
  #searchStreets(searchTerm: string): Observable<string[]> {
    return this.#swissPostService
      .searchStreets(searchTerm, {
        onrp: this.#selectedOnrp,
        zipCode: this.values.postal_code,
        townName: this.values.city,
      })
      .pipe(
        tap(({ lookupMap }) => {
          this.#streetLookup.clear();
          lookupMap.forEach((value, key) => this.#streetLookup.set(key, value));
        }),
        map(({ displayStrings }) => displayStrings)
      );
  }

  /**
   * Verify address using Swiss Post API service
   */
  #verifyAddress(): Promise<AddressSuggestion | null> {
    const { address, address_2, postal_code, city } = this.values;
    if (!postal_code || !city || !address || !address_2) {
      return Promise.resolve(null);
    }
    return this.#swissPostService.verifyAddress(address, address_2, postal_code, city);
  }

  /**
   * Search for alternative addresses when verification fails
   */
  #searchAlternativeAddresses(): Promise<AddressSuggestion[]> {
    const { address, address_2, postal_code, city } = this.values;
    if (!postal_code || !city || !address || !address_2) {
      return Promise.resolve([]);
    }
    return this.#swissPostService.searchAlternativeAddresses({
      streetName: address,
      houseNo: address_2,
      onrp: this.#selectedOnrp,
      zipCode: postal_code,
      townName: city,
      strid: this.#selectedStrid,
    });
  }

  set value(val: Record<string, string>) {
    this.onTouch(val);
    this.onChange(val);
    this.#cdr.detectChanges();
  }

  onChange: (value: Record<string, string>) => void = () => {};
  onTouch: (value: Record<string, string>) => void = () => {};

  writeValue(value: Record<string, string> | null): void {
    if (!value) return;
    this.value = value;
    this.values.address = value.address || '';
    this.parentForm.get(this.addressId)?.setValue(value.address || '');
    this.values.address_2 = value.address_2 || '';
    this.parentForm.get(this.address2Id)?.setValue(value.address_2 || '');

    // Set combined postal_city display value
    this.values.city = value.city || '';
    this.values.postal_code = value.postal_code || '';

    // Preserve postal_city if explicitly set, otherwise reconstruct from postal_code + city
    const postalCityDisplay =
      value.postal_city !== undefined
        ? value.postal_city
        : value.postal_code && value.city
          ? `${value.postal_code} ${value.city}`
          : '';
    this.values.postal_city = postalCityDisplay;
    this.parentForm.get(this.postalCityId)?.setValue(postalCityDisplay);

    if (value.postal_code) {
      this.#ga.selectGa(value.postal_code, this.#autofill.loginControlUsed());
    }

    // Re-validate postal_city control after values change
    this.parentForm.get(this.postalCityId)?.updateValueAndValidity();
    this.#cdr.markForCheck();
  }

  registerOnChange(fn: (value: Record<string, string>) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: Record<string, string>) => void): void {
    this.onTouch = fn;
  }

  setAddress(address: AddressSuggestion): void {
    this.writeValue({
      address: address.streetName,
      address_2: address.houseNo + (address.houseNoAddition || ''),
      city: address.townName,
      postal_code: address.zipCode,
    });

    this.popover = false;
    this.alternativeAddresses = [];
  }

  async handleChange(key: string, value: string): Promise<void> {
    this.popover = false;
    this.alternativeAddresses = [];
    this.values[key] = value;

    // Handle combined postal_city selection
    if (key === 'postal_city') {
      if (value) {
        const locationData = this.#locationLookup.get(value);
        if (locationData) {
          // User selected from dropdown - valid selection
          this.values.postal_code = locationData.zipCode;
          this.values.city = locationData.townName;
          this.values.postal_city = locationData.display;
          this.#selectedOnrp = locationData.onrp || '';
          this.#selectedStrid = '';
          this.#lastLocationSearchEmpty = false;
          if (locationData.zipCode) {
            this.#ga.selectGa(locationData.zipCode, this.#autofill.loginControlUsed());
          }
        } else {
          // User typed a value without selecting from dropdown - clear underlying values
          // Field will be invalid until a valid selection is made
          this.values.postal_code = '';
          this.values.city = '';
          this.values.postal_city = value;
          this.#selectedOnrp = '';
        }
      } else {
        // Clear underlying values when postal_city is emptied
        this.values.postal_code = '';
        this.values.city = '';
        this.values.postal_city = '';
        this.#selectedOnrp = '';
        this.#lastLocationSearchEmpty = false;
      }
    }

    // Handle street selection - look up STRID for building lookup
    if (key === 'address' && value) {
      const streetData = this.#streetLookup.get(value);
      if (streetData) {
        this.values.address = streetData.streetName;
        this.#selectedStrid = streetData.strid;
      }
    }

    // Verify complete address when all fields are filled
    await this.#verifyAndSuggestAddress();

    setTimeout$(() => this.writeValue(this.values), 5);
  }

  /**
   * Verify address and show suggestions if needed
   */
  async #verifyAndSuggestAddress(): Promise<void> {
    const { city, postal_code, address, address_2 } = this.values;
    const hasLocation =
      (city?.trim().length ?? 0) > 2 || (postal_code?.trim().length ?? 0) > 2;
    const hasStreet = (address?.trim().length ?? 0) > 2;
    const hasBuilding = (address_2?.trim().length ?? 0) >= 1;

    if (!hasLocation || !hasStreet || !hasBuilding) return;

    const verified = await this.#verifyAddress();

    if (verified) {
      const { isCorrected, isAmbiguous } = this.#swissPostService.parsePstat(
        verified.pstat
      );
      if (isCorrected) {
        this.alternativeAddresses = [verified];
        this.popover = true;
      } else if (isAmbiguous) {
        this.alternativeAddresses = await this.#searchAlternativeAddresses();
        this.popover = this.alternativeAddresses.length > 0;
      }
    } else {
      this.alternativeAddresses = await this.#searchAlternativeAddresses();
      this.popover = this.alternativeAddresses.length > 0;
    }

    this.#cdr.detectChanges();
  }

  get id(): string {
    return this.field['#webform_key'];
  }

  get addressId(): string {
    return `${this.id}-generated_address`;
  }

  get address2Id(): string {
    return `${this.id}-generated_address_2`;
  }

  get postalCityId(): string {
    return `${this.id}-generated_postal_city`;
  }
}
